import type { ClockMonthNote } from '@felix/domain';
import type { TFunction } from 'i18next';
import type {
  CanvasElement,
  Content,
  ContentStack,
  ContentText,
  CustomTableLayout,
  TableCell,
  TDocumentDefinitions,
} from 'pdfmake/interfaces.js';
import { formatHours } from '../../format.js';
import { TOKENS } from '../../tokens.js';
import { clockNoteText, type MonthRow } from './rest-month.js';
import type { ShipTime } from './ship-clock.js';
import type { RestMonth } from './types.js';

/**
 * Aylık dinlenme kaydının PDF kopyası (ADR 0024 #12): ILO/IMO "record of hours of work or hours
 * of rest" biçimi, A4 yatay, bir gemi adamı, bir ay. Gemi adamı kaptan ve kendisi tarafından
 * onaylanmış bir kopya alır [MLC A2.3 §12]; kayıt İngilizce ve gerekiyorsa geminin çalışma
 * dilinde tutulur [MLC A2.3 §10].
 *
 * Saf fonksiyon: aylık takvimin verisinden (`monthTable` satırları ve `RestMonth`) pdfmake belge
 * tanımı üretir. Saat ve dil parametreyle gelir; aynı girdi aynı tanımı verir. Gemi uygulaması ve
 * web ofis aynı fonksiyonu kullanır, böylece iki yerde aynı çıktı oluşur.
 */

/** Bir dilin metin fonksiyonu (i18next `getFixedT`). */
export type PdfT = (key: string, options?: Record<string, unknown>) => string;

export interface PdfLanguage {
  /** `en`, `tr`: ay adı için. */
  locale: string;
  t: PdfT;
}

/** i18next'in sabit dilli `t` fonksiyonundan PDF dili (ör. `i18n.getFixedT('en')`). */
export function pdfLanguage(t: TFunction, locale: string): PdfLanguage {
  return { locale, t: (key, options) => t(key, options ?? {}) };
}

export interface PdfLanguages {
  /** Her zaman İngilizce [MLC A2.3 §10]. */
  primary: PdfLanguage;
  /** Geminin çalışma dili İngilizce değilse o dil; etiketler iki dilli olur. */
  secondary?: PdfLanguage | null;
}

export interface RestMonthPdfInput {
  vessel: { name: string; imo: string | null };
  seafarer: { fullName: string; rank: string | null };
  /** `YYYY-MM`, gemi yerel ayı. */
  month: string;
  /** Geminin IANA saat dilimi ya da saat kaydının çizelgesi; aralıklar gemi saatiyle. */
  timeZone: ShipTime;
  /** Ayın gemi saati notları (ADR 0065 §7): kaydın altında otomatik açıklama. */
  clockNotes?: readonly ClockMonthNote[];
  /** Aylık takvimin satırları (`monthTable`). */
  rows: readonly MonthRow[];
  record: Pick<RestMonth, 'contentHash' | 'state' | 'submissions' | 'approvals'>;
  /** İmzalayanların adları (kullanıcı kimliğine göre). */
  names: Readonly<Record<string, string>>;
  /** Üretim zamanı, ISO-8601 UTC; altbilgide ve PDF bilgisinde. */
  generatedAt: string;
}

/** Izgarada bir saatin genişliği (pt): 24 saat × 14 = 336 pt. */
export const PDF_GRID_HOUR = 14;
const GRID_WIDTH = 24 * PDF_GRID_HOUR;
const BAR_HEIGHT = 6;
const FONT = 'Roboto';

const prefix = 'restHours.pdf';

/** `YYYY-MM-DDTHH:MM…Z` → `YYYY-MM-DD HH:MM UTC`: dilden bağımsız, iki uygulamada aynı. */
function utc(at: string): string {
  return `${at.slice(0, 10)} ${at.slice(11, 16)} UTC`;
}

function clock(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

/** Not tarihi: "16 Oct 2026" (dile göre kısa ay; ortağın örneği). */
function pdfDate(date: string, locale: string): string {
  // İngilizcede en-US ay adı ("Sep"; en-GB "Sept"), gün önce
  const month = new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : locale, {
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`));
  return `${Number(date.slice(8, 10))} ${month} ${date.slice(0, 4)}`;
}

function monthName(month: string, locale: string): string {
  return new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : locale, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${month}-01T00:00:00Z`));
}

/** Dosya adı: ASCII, boşluksuz (ör. `hours-of-rest-2026-09-cagri-sahin.pdf`). */
export function restMonthPdfFileName(
  input: Pick<RestMonthPdfInput, 'month' | 'seafarer'>,
  t: PdfT,
): string {
  const name = input.seafarer.fullName
    .toLocaleLowerCase('tr')
    .replace(/ı/g, 'i')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${t(`${prefix}.fileName`)}-${input.month}${name ? `-${name}` : ''}.pdf`;
}

/** İnce çizgili tablo düzeni. */
const GRID_LAYOUT: CustomTableLayout = {
  hLineWidth: () => 0.4,
  vLineWidth: () => 0.4,
  hLineColor: () => TOKENS['activity-rest'],
  vLineColor: () => TOKENS['activity-rest'],
  paddingLeft: () => 2,
  paddingRight: () => 2,
  paddingTop: () => 1,
  paddingBottom: () => 1,
};

/**
 * Günün 24 saatlik ızgarası: çalışma dakika hassasiyetinde taralı; acil durum ayrı renkte.
 * Yaşanmayan duvar saati (ileri alma, atlanan gün) çapraz çizgilerle taralı (ADR 0065 §7).
 */
function dayGrid(
  work: MonthRow['work'],
  notLived: readonly { from: number; to: number }[] = [],
): Content {
  const elements: CanvasElement[] = [
    {
      type: 'rect',
      x: 0,
      y: 0,
      w: GRID_WIDTH,
      h: BAR_HEIGHT,
      lineWidth: 0.4,
      lineColor: TOKENS['activity-rest'],
    },
  ];
  for (let hour = 1; hour < 24; hour++) {
    elements.push({
      type: 'line',
      x1: hour * PDF_GRID_HOUR,
      y1: hour % 6 === 0 ? 0 : BAR_HEIGHT / 2,
      x2: hour * PDF_GRID_HOUR,
      y2: BAR_HEIGHT,
      lineWidth: hour % 6 === 0 ? 0.5 : 0.3,
      lineColor: TOKENS['activity-rest'],
    });
  }
  for (const gap of notLived) {
    const x0 = (gap.from / 60) * PDF_GRID_HOUR;
    const x1 = (gap.to / 60) * PDF_GRID_HOUR;
    for (let x = x0; x < x1; x += 2.5) {
      elements.push({
        type: 'line',
        x1: x,
        y1: BAR_HEIGHT,
        x2: Math.min(x + BAR_HEIGHT, x1),
        y2: Math.max(0, BAR_HEIGHT - (Math.min(x + BAR_HEIGHT, x1) - x)),
        lineWidth: 0.3,
        lineColor: TOKENS['text-secondary'],
      });
    }
  }
  for (const w of work) {
    elements.push({
      type: 'rect',
      x: (w.from / 60) * PDF_GRID_HOUR,
      y: 0,
      w: ((w.to - w.from) / 60) * PDF_GRID_HOUR,
      h: BAR_HEIGHT,
      color: w.activity === 'emergency' ? TOKENS['danger-fg'] : TOKENS['navy-900'],
    });
  }
  return { canvas: elements };
}

/**
 * pdfmake belge tanımı. Etiketler `restHours.pdf.*` metinlerinden; ikinci dil verilirse her etiket
 * İngilizcenin altında ikinci dilde yazılır.
 */
export function restMonthPdf(
  input: RestMonthPdfInput,
  languages: PdfLanguages,
): TDocumentDefinitions {
  const { primary } = languages;
  const secondary = languages.secondary ?? null;
  const t = primary.t;

  /** Etiket: İngilizce, ikinci dil varsa altında. */
  const label = (key: string, options?: Record<string, unknown>): ContentText | ContentStack => {
    const first = t(`${prefix}.${key}`, options);
    if (!secondary) return { text: first };
    return {
      stack: [{ text: first }, { text: secondary.t(`${prefix}.${key}`, options), style: 'second' }],
    };
  };
  /** Satır içi iki dilli metin: `İngilizce / ikinci dil`. */
  const inline = (key: string, options?: Record<string, unknown>): string => {
    const first = t(key, options);
    return secondary ? `${first} / ${secondary.t(key, options)}` : first;
  };

  const { record } = input;
  const status = record.state.status;
  const statusTone =
    status === 'approved'
      ? TOKENS['success-fg']
      : status === 'returned'
        ? TOKENS['danger-fg']
        : TOKENS['warning-fg'];

  // Başlık bilgileri (ILO/IMO biçimi: gemi, gemi adamı, görev, ay)
  const monthValue: Content = secondary
    ? {
        stack: [
          { text: monthName(input.month, primary.locale) },
          { text: monthName(input.month, secondary.locale), style: 'second' },
        ],
      }
    : { text: monthName(input.month, primary.locale) };
  const statusValue: Content = {
    text: inline(`restHours.month.status.${status}`),
    bold: true,
    color: statusTone,
  };
  const info: TableCell[][] = [
    [
      { ...label('vessel'), style: 'key' },
      { text: input.vessel.name, bold: true },
      { ...label('seafarer'), style: 'key' },
      { text: input.seafarer.fullName, bold: true },
      { ...label('month'), style: 'key' },
      { ...monthValue, bold: true },
    ],
    [
      { ...label('imo'), style: 'key' },
      { text: input.vessel.imo ?? '—' },
      { ...label('rank'), style: 'key' },
      { text: input.seafarer.rank ?? '—' },
      { ...label('timeZone'), style: 'key' },
      { text: typeof input.timeZone === 'string' ? input.timeZone : input.timeZone.timeZone },
    ],
    [{ ...label('status'), style: 'key' }, { ...statusValue, colSpan: 5 }, {}, {}, {}, {}],
  ];

  // Günlük tablo
  const hourLabels: Content = {
    columns: Array.from({ length: 24 }, (_, hour) => ({
      text: String(hour).padStart(2, '0'),
      width: PDF_GRID_HOUR,
      alignment: 'center' as const,
      fontSize: 5,
    })),
    columnGap: 0,
  };
  const head: TableCell[] = [
    { ...label('day'), style: 'head' },
    { stack: [label('grid'), hourLabels], style: 'head' },
    { ...label('periods'), style: 'head' },
    { ...label('work'), style: 'head' },
    { ...label('rest'), style: 'head' },
    { ...label('rest24h'), style: 'head' },
    { ...label('rest7d'), style: 'head' },
    { ...label('remarks'), style: 'head' },
  ];
  const hours = (value: number | null): ContentText => ({
    text: value === null ? '—' : formatHours(value),
    alignment: 'right',
  });
  const body: TableCell[][] = [head];
  let workTotal = 0;
  let restTotal = 0;
  let recorded = 0;
  let violations = 0;
  let warnings = 0;
  let exceptions = 0;
  for (const row of input.rows) {
    if (row.status !== null) recorded++;
    if (row.status === 'violation') violations++;
    if (row.status === 'warning') warnings++;
    exceptions += row.findings.filter((f) => f.exception).length;
    workTotal += row.workHours ?? 0;
    restTotal += row.restHours ?? 0;

    // İşaretler: V ihlal, W uyarı, E istisna [MLC A2.3 §14]
    const marks: string[] = [];
    if (row.status === 'violation' || row.findings.some((f) => f.severity === 'violation')) {
      marks.push('V');
    }
    if (row.status === 'warning' || row.findings.some((f) => f.severity === 'warning')) {
      marks.push('W');
    }
    if (row.exception || row.findings.some((f) => f.exception)) marks.push('E');
    const rules = [...new Set(row.findings.map((f) => f.rule))].map((rule) =>
      inline(`${prefix}.rules.${rule}`),
    );
    // Gemi saati: günün süresi ve tarih çizgisi (ADR 0065 §7)
    const clockRemark = row.skippedDay
      ? inline('restHours.clock.dateSkipped')
      : row.occurrence
        ? inline('restHours.clock.dateRepeated')
        : row.minutes !== undefined && row.minutes !== 1440
          ? inline('restHours.clock.dayLength', {
              hours:
                row.minutes % 60 === 0
                  ? row.minutes / 60
                  : `${Math.floor(row.minutes / 60)}:${String(row.minutes % 60).padStart(2, '0')}`,
            })
          : null;
    let remarks: TableCell;
    if (row.skippedDay) {
      remarks = { text: clockRemark ?? '', color: TOKENS['text-secondary'] };
    } else if (row.status === null) {
      remarks = { text: inline(`${prefix}.notRecorded`), color: TOKENS['text-secondary'] };
    } else if (marks.length === 0) {
      remarks = { text: '' };
    } else {
      remarks = {
        text: [
          {
            text: marks.join(' '),
            bold: true,
            color: marks.includes('V') ? TOKENS['danger-fg'] : TOKENS['warning-fg'],
          },
          ...(rules.length > 0 ? [{ text: `  ${rules.join('; ')}` }] : []),
        ],
      };
    }
    if (clockRemark && !row.skippedDay) {
      const before = (remarks as { text: unknown }).text;
      remarks = {
        text: [
          ...(Array.isArray(before) ? before : before ? [{ text: String(before) }] : []),
          { text: `${before ? '  ' : ''}${clockRemark}`, color: TOKENS['info-fg'] },
        ],
      };
    }
    const fill =
      row.status === 'violation'
        ? TOKENS['danger-bg']
        : row.status === 'warning'
          ? TOKENS['warning-bg']
          : undefined;
    const cells: TableCell[] = [
      {
        text: row.occurrence ? `${row.date.slice(8, 10)}/${row.occurrence}` : row.date.slice(8, 10),
        alignment: 'center',
      },
      dayGrid(row.work, row.skippedDay ? [{ from: 0, to: 1440 }] : (row.notLived ?? [])),
      { text: row.work.map((w) => `${clock(w.from)}–${clock(w.to)}`).join(', ') },
      hours(row.workHours),
      hours(row.restHours),
      hours(row.rest24h),
      hours(row.rest7d),
      remarks,
    ];
    body.push(
      fill ? cells.map((cell) => ({ ...(cell as object), fillColor: fill }) as TableCell) : cells,
    );
  }
  body.push([
    { ...label('total'), colSpan: 3, bold: true },
    {},
    {},
    { ...hours(workTotal), bold: true },
    { ...hours(restTotal), bold: true },
    {
      ...label('summary', { recorded, violations, warnings, count: exceptions }),
      colSpan: 3,
    },
    {},
    {},
  ]);

  // Sistemdeki imzalar: son gemi adamı imzası ve ondan sonraki kaptan kararı (ADR 0021 §3)
  const name = (userId: string) => input.names[userId] ?? userId;
  const latest = <T extends { at: string }>(items: readonly T[]): T | null =>
    items.reduce<T | null>(
      (last, item) => (last === null || item.at > last.at ? item : last),
      null,
    );
  const submission = latest(record.submissions);
  const approval = latest(
    record.approvals.filter((a) => submission === null || a.at >= submission.at),
  );
  /** İmza kaydın şimdiki içeriğine mi ait; değilse onaylı görünmez (ADR 0021 §2). */
  const signatureLine = (key: string, options: Record<string, unknown>, hash: string): Content => {
    const earlier = hash !== record.contentHash;
    const lineFor = (language: PdfLanguage) =>
      `${language.t(`${prefix}.${key}`, options)} · ${hash.slice(0, 8)}${
        earlier ? ` — ${language.t(`${prefix}.earlier`)}` : ''
      }`;
    const first: Content = {
      text: lineFor(primary),
      color: earlier ? TOKENS['danger-fg'] : undefined,
    };
    return secondary ? { stack: [first, { text: lineFor(secondary), style: 'second' }] } : first;
  };
  const electronic: Content[] = [
    { ...label('electronic'), style: 'section' },
    submission
      ? signatureLine(
          'seafarerSigned',
          { name: name(submission.by), at: utc(submission.at), revision: submission.revision },
          submission.contentHash,
        )
      : label('notSigned'),
    approval
      ? signatureLine(
          approval.decision === 'approved' ? 'masterApproved' : 'masterReturned',
          { name: name(approval.by), at: utc(approval.at) },
          approval.contentHash,
        )
      : label('notApproved'),
    { ...label('hash'), style: 'key', margin: [0, 4, 0, 0] },
    { text: record.contentHash, fontSize: 6.5, characterSpacing: 0.2 },
  ];

  // Islak imza kutuları (ILO/IMO biçimi: kaptan ya da yetkilendirdiği kişi, gemi adamı): ad,
  // imza ve tarih için altı çizili boş alan
  const box = (key: string): TableCell => ({
    stack: [
      { ...label(key), bold: true },
      {
        table: {
          widths: ['*', '*', 64],
          body: [
            [
              { ...label('name'), style: 'key' },
              { ...label('signature'), style: 'key' },
              { ...label('date'), style: 'key' },
            ],
          ],
        },
        layout: {
          hLineWidth: (i: number) => (i === 0 ? 0 : 0.4),
          vLineWidth: () => 0,
          hLineColor: () => TOKENS['activity-rest'],
          paddingTop: () => 2,
          paddingBottom: () => 16,
          paddingLeft: () => 0,
          paddingRight: () => 8,
        },
      },
    ],
  });
  const wet: Content[] = [
    { ...label('declaration'), bold: true, margin: [0, 0, 0, 3] },
    {
      table: { widths: ['*', '*'], body: [[box('masterBox'), box('seafarerBox')]] },
      layout: GRID_LAYOUT,
    },
  ];
  electronic.push({ ...label('copyNote'), style: 'key', margin: [0, 4, 0, 0] });

  const at = utc(input.generatedAt);
  return {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [24, 18, 24, 24],
    info: {
      title: `${t(`${prefix}.title`)} — ${input.seafarer.fullName} — ${input.month}`,
      author: 'Felixmate',
      subject: input.vessel.name,
      creator: 'Felixmate',
      producer: 'Felixmate',
      creationDate: new Date(input.generatedAt),
    },
    defaultStyle: { font: FONT, fontSize: 6.5, lineHeight: 1, color: TOKENS['navy-900'] },
    styles: {
      title: { fontSize: 12, bold: true },
      subtitle: { fontSize: 7, color: TOKENS['text-secondary'] },
      key: { fontSize: 6, color: TOKENS['text-secondary'] },
      head: { fontSize: 6, bold: true },
      second: { fontSize: 5.5, italics: true, color: TOKENS['text-secondary'], bold: false },
      section: { fontSize: 7.5, bold: true, margin: [0, 0, 0, 2] },
    },
    footer: (page: number, pages: number): Content => ({
      // Her sayfa kaydı tanıtır: imzalar ikinci sayfaya düşse de sayfa ayrılmaz
      text: t(`${prefix}.footer`, {
        name: input.seafarer.fullName,
        month: input.month,
        at,
        page,
        pages,
      }),
      fontSize: 6,
      color: TOKENS['text-secondary'],
      alignment: 'right',
      margin: [24, 7, 24, 0],
    }),
    content: [
      {
        columns: [
          {
            width: '*',
            // Alt başlık kural kaynaklarıdır; ikinci dilde tekrarlanmaz
            stack: [
              { ...label('title'), style: 'title' },
              { text: t(`${prefix}.subtitle`), style: 'subtitle' },
            ],
          },
        ],
        margin: [0, 0, 0, 4],
      },
      {
        table: { widths: [70, '*', 78, '*', 66, '*'], body: info },
        layout: GRID_LAYOUT,
        margin: [0, 0, 0, 5],
      },
      {
        table: {
          headerRows: 1,
          dontBreakRows: true,
          widths: [16, GRID_WIDTH, 112, 30, 30, 34, 34, '*'],
          body,
        },
        layout: GRID_LAYOUT,
      },
      { ...label('legend'), style: 'key', margin: [0, 2, 0, 4] },
      // Gemi saati notları (ADR 0065 §7): "Ship's clocks retarded by 60 minutes on …"
      ...(input.clockNotes && input.clockNotes.length > 0
        ? [
            {
              stack: [
                { ...label('clockNotes'), bold: true },
                ...input.clockNotes.map((note) => ({
                  text: [
                    clockNoteText(note, t, (date) => pdfDate(date, primary.locale)),
                    ...(secondary
                      ? [
                          ` / ${clockNoteText(note, secondary.t, (date) => pdfDate(date, secondary.locale))}`,
                        ]
                      : []),
                  ].join(''),
                })),
              ],
              style: 'key',
              margin: [0, 0, 0, 4] as [number, number, number, number],
            },
          ]
        : []),
      {
        columns: [
          { width: 280, stack: electronic },
          { width: '*', stack: wet },
        ],
        columnGap: 14,
        unbreakable: true,
      },
    ],
  };
}
